import { useState, useEffect } from "react";
import WizardShell from "@/components/WizardShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import PhotoUpload from "@/components/PhotoUpload";
import Repeater from "@/components/Repeater";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import usePostcodeLookup from "@/lib/usePostcodeLookup";

const houseNr = (addr) => (String(addr || "").match(/\s(\d+\s*[A-Za-z0-9-]*)\s*$/) || [])[1] || "";
import api from "@/lib/api";
import { toast } from "sonner";
import { normalizeNlPhone } from "@/lib/format";

const empty = {
  client_number: "", name: "", type: "bedrijf", contact_person: "", contact_person2: "",
  contact_function: "", contact2_function: "", contacts: [],
  email: "", phone: "", email2: "", phone2: "", address: "", postal_code: "", city: "",
  billing_address: "", billing_phone: "", billing_email: "", rate: "", special_terms: "",
  asset_managers: [], account_managers: [], bank_accounts: [], custom_fields: [],
  notes: "", photo_url: "", grace_days: 0,
};

const personFields = [
  { key: "name", label: "Naam", full: true },
  { key: "mobile", label: "Mobiel" },
  { key: "phone", label: "Telefoon" },
  { key: "email", label: "E-mail", full: true },
];

export default function ClientWizard({ open, onOpenChange, onSaved, initial }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const lookup = usePostcodeLookup((a, nr) => setForm((f) => ({ ...f, address: `${a.street} ${nr}`, city: a.city })));

  useEffect(() => {
    if (open) { setForm(initial?.id ? { ...empty, ...initial } : empty); setStep(0); }
  }, [open]);

  const finish = async () => {
    setLoading(true);
    try {
      if (initial?.id) await api.put(`/clients/${initial.id}`, form);
      else await api.post("/clients", form);
      toast.success("Opdrachtgever opgeslagen");
      onSaved?.();
      onOpenChange(false);
      setStep(0); setForm(empty);
    } catch (e) {
      toast.error("Opslaan mislukt");
    } finally { setLoading(false); }
  };

  return (
    <WizardShell open={open} onOpenChange={onOpenChange}
      title={initial?.id ? "Opdrachtgever bewerken" : "Nieuwe Opdrachtgever"}
      subtitle="Stamgegevens van de opdrachtgever"
      steps={["Gegevens", "Facturatie", "Beheerders & Bank", "Extra & Notities"]}
      step={step} setStep={setStep} onFinish={finish} loading={loading}
      canNext={step !== 0 || form.name.trim().length > 0}
      finishLabel="Opdrachtgever opslaan">
      {step === 0 && (
        <div className="space-y-5">
          <div>
            <Label>Type opdrachtgever</Label>
            <RadioGroup value={form.type} onValueChange={(v) => set("type", v)} className="grid grid-cols-2 gap-3 mt-2">
              {[["bedrijf", "Bedrijf / Beheerkantoor"], ["particulier", "Particulier"]].map(([v, l]) => (
                <label key={v} className={`flex items-center gap-2 border rounded-lg p-3 cursor-pointer ${form.type === v ? "ring-2 ring-[#C9A227]/50 border-[#0F1E3D] bg-[#0F1E3D]/5" : "border-slate-200"}`}>
                  <RadioGroupItem value={v} data-testid={`client-type-${v}`} /> <span className="text-sm">{l}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label htmlFor="cw-num">Klantnummer / boekhouding</Label><Input id="cw-num" data-testid="client-number-input" value={form.client_number} onChange={(e) => set("client_number", e.target.value)} placeholder="bijv. 1001" /></div>
            <div><Label htmlFor="cw-name">Naam {form.type === "bedrijf" ? "bedrijf" : "opdrachtgever"} *</Label><Input id="cw-name" data-testid="client-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="bijv. Vastgoed Amsterdam B.V." /></div>
          </div>
          <div className="border rounded-lg p-3">
            <Label className="text-xs font-semibold text-slate-600 mb-2 block">Primair contactpersoon</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Naam</Label><Input data-testid="client-contact-input" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
              <div><Label className="text-xs">Functie</Label><Input data-testid="client-contact-function-input" value={form.contact_function} onChange={(e) => set("contact_function", e.target.value)} placeholder="bijv. Beheerder" /></div>
              <div><Label className="text-xs">E-mail</Label><Input data-testid="client-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><Label className="text-xs">Telefoon</Label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} onBlur={(e) => set("phone", normalizeNlPhone(e.target.value))} /></div>
            </div>
          </div>
          <div className="border rounded-lg p-3">
            <Label className="text-xs font-semibold text-slate-600 mb-2 block">Secundair contactpersoon</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Naam</Label><Input data-testid="client-contact2-input" value={form.contact_person2} onChange={(e) => set("contact_person2", e.target.value)} /></div>
              <div><Label className="text-xs">Functie</Label><Input data-testid="client-contact2-function-input" value={form.contact2_function} onChange={(e) => set("contact2_function", e.target.value)} placeholder="bijv. Boekhouding" /></div>
              <div><Label className="text-xs">E-mail</Label><Input data-testid="client-email2-input" value={form.email2} onChange={(e) => set("email2", e.target.value)} /></div>
              <div><Label className="text-xs">Telefoon</Label><Input data-testid="client-phone2-input" value={form.phone2} onChange={(e) => set("phone2", e.target.value)} onBlur={(e) => set("phone2", normalizeNlPhone(e.target.value))} /></div>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-2 block">Extra contactpersonen</Label>
            <Repeater items={form.contacts} onChange={(v) => set("contacts", v)} testid="client-contacts"
              fields={[{ key: "name", label: "Naam", full: true }, { key: "function", label: "Functie" }, { key: "phone", label: "Telefoon", normalize: true }, { key: "email", label: "E-mail", full: true }]}
              addLabel="Contactpersoon toevoegen" emptyHint="Nog geen extra contactpersonen." />
          </div>
          <AddressAutocomplete label="Zoek adres (autocomplete)" onSelect={(a) => setForm((f) => ({ ...f, address: `${a.street} ${a.number}`.trim(), postal_code: a.postal_code, city: a.city }))} />

          <div><Label>Adres</Label><Input data-testid="client-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} onBlur={(e) => { const nr = houseNr(e.target.value); lookup(form.postal_code, nr, nr); }} placeholder="Straat + huisnummer" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Postcode</Label><Input data-testid="client-postal-input" value={form.postal_code} onChange={(e) => { set("postal_code", e.target.value); const nr = houseNr(form.address); lookup(e.target.value, nr, nr); }} /></div>
            <div><Label>Plaats</Label><Input data-testid="client-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
          </div>
        </div>
      )}
      {step === 1 && (
        <div className="space-y-5">
          <AddressAutocomplete label="Zoek facturatieadres (autocomplete)" onSelect={(a) => set("billing_address", a.full)} />
          <div><Label>Correspondentieadres (facturatie)</Label><Input data-testid="client-billing-address-input" value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Telefoon (facturatie)</Label><Input data-testid="client-billing-phone-input" value={form.billing_phone} onChange={(e) => set("billing_phone", e.target.value)} onBlur={(e) => set("billing_phone", normalizeNlPhone(e.target.value))} /></div>
            <div><Label>E-mail (facturatie)</Label><Input data-testid="client-billing-email-input" value={form.billing_email} onChange={(e) => set("billing_email", e.target.value)} /></div>
          </div>
          <div><Label>Tarief</Label><Input data-testid="client-rate-input" value={form.rate} onChange={(e) => set("rate", e.target.value)} placeholder="bijv. 15% of € 12,50 per handeling" /></div>
          <div><Label>Bijzondere afspraken</Label><Textarea rows={3} data-testid="client-special-terms-input" value={form.special_terms} onChange={(e) => set("special_terms", e.target.value)} /></div>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <Label className="mb-2 block">Assetmanagers</Label>
            <Repeater items={form.asset_managers} onChange={(v) => set("asset_managers", v)} fields={personFields} addLabel="Assetmanager toevoegen" emptyHint="Nog geen assetmanagers." testid="client-assetmanagers" />
          </div>
          <div>
            <Label className="mb-2 block">Accountmanagers</Label>
            <Repeater items={form.account_managers} onChange={(v) => set("account_managers", v)} fields={personFields} addLabel="Accountmanager toevoegen" emptyHint="Nog geen accountmanagers." testid="client-accountmanagers" />
          </div>
          <div>
            <Label className="mb-2 block">Bankrekeningen</Label>
            <Repeater items={form.bank_accounts} onChange={(v) => set("bank_accounts", v)} fields={[{ key: "iban", label: "IBAN" }, { key: "name_tag", label: "Naam / label" }]} addLabel="Bankrekening toevoegen" emptyHint="Nog geen bankrekeningen." testid="client-banks" />
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-5">
          <div>
            <Label className="mb-2 block">Extra velden (vrij)</Label>
            <Repeater items={form.custom_fields} onChange={(v) => set("custom_fields", v)} fields={[{ key: "label", label: "Veldnaam" }, { key: "value", label: "Waarde" }]} addLabel="Extra veld toevoegen" emptyHint="Voeg vrije velden toe zoals KvK-nummer, portefeuille, enz." testid="client-custom-fields" />
          </div>
          <PhotoUpload value={form.photo_url} onChange={(v) => set("photo_url", v)} label="Foto / logo van opdrachtgever" allowUrl testid="client-photo" />
          <div>
            <Label>Standaard grace-periode (dagen)</Label>
            <Input type="number" min={0} data-testid="client-grace-input" value={form.grace_days} onChange={(e) => set("grace_days", Number(e.target.value) || 0)} />
          </div>
          <div><Label>Interne notities</Label><Textarea data-testid="client-notes-input" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Afspraken, bijzonderheden..." /></div>
        </div>
      )}
    </WizardShell>
  );
}
